import { Alert, Skeleton } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { GraphicPageTree } from '../graphicPageTypes'
import GraphicTreeRenderer, { type ImageResolver } from '../workspace/website/graphicPages/GraphicTreeRenderer'
import { fetchPublicGraphicPage, type PublicApiError } from './publicApi'

/**
 * Public Renderer for a published Graphic Page (spec §19/§43/§44 — the
 * hard Snapshot-only acceptance item). Reads exclusively from
 * `GraphicPage.published_snapshot_json` via the backend's
 * `get_graphic_page` route; a Draft edit made after this snapshot was
 * taken must NOT be visible here until the page is re-published — that
 * is enforced server-side (`graphic_page_service.get_published_tree`
 * only returns `published_snapshot_json`), this component just renders
 * whatever tree the API returns.
 *
 * Device (`desktop`/`mobile`) is derived from real viewport width — a
 * public visitor gets no manual toggle, just a responsive render that
 * picks the correct Hero/Cover crop for their actual device (spec §15
 * "不要把 Mobile 做成 Desktop 的简单缩小").
 */
export default function GraphicPagePublicDetail({ workspaceSlug, siteSlug }: { workspaceSlug: string; siteSlug: string }) {
  const { slug = '' } = useParams()
  const [tree, setTree] = useState<GraphicPageTree | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [device, setDevice] = useState<'desktop' | 'mobile'>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop'))

  useEffect(() => {
    setTree(null)
    setError(null)
    fetchPublicGraphicPage(workspaceSlug, siteSlug, slug)
      .then((r) => setTree(r.data))
      .catch((err) => setError((err as PublicApiError).message))
  }, [workspaceSlug, siteSlug, slug])

  useEffect(() => {
    const onResize = () => setDevice(window.innerWidth < 768 ? 'mobile' : 'desktop')
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const resolveImage: ImageResolver = useMemo(
    () => ({
      block: (block, role) => (role === 'original' ? block.asset_original_url ?? null : block.asset_url ?? null),
      page: (page, role) => (role === 'original' ? null : page.cover_url ?? null),
      entryCover: (entry) => entry.cover_url ?? null,
    }),
    [],
  )

  if (error) {
    return (
      <div className="pub-page">
        <Alert type="error" message={error === 'graphic page not published' ? '该页面尚未发布或不存在' : error} showIcon />
      </div>
    )
  }
  if (!tree) return <div className="pub-page"><Skeleton active paragraph={{ rows: 10 }} /></div>

  return <GraphicTreeRenderer tree={tree} device={device} resolveImage={resolveImage} />
}
